import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';

const ORGANISME_META = {
  SAR: { nom: 'SAR', heading: 'text-honey-800', card: 'border-honey-200' },
  AMAIR: { nom: 'AMAIR', heading: 'text-green-800', card: 'border-green-200' },
  ADAR: { nom: 'ADAR', heading: 'text-blue-800', card: 'border-blue-200' },
};

export default function AcceptationDocuments() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [documents, setDocuments] = useState(user?.documentsAAccepter || []);
  const [checked, setChecked] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/documents-statutaires/pending`
        );
        if (!cancelled) {
          setDocuments(data.documents || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.message ||
              'Impossible de charger les documents à accepter.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const grouped = useMemo(() => {
    const groups = {};
    for (const doc of documents) {
      if (!groups[doc.organisme]) groups[doc.organisme] = [];
      groups[doc.organisme].push(doc);
    }
    return groups;
  }, [documents]);

  const allChecked =
    documents.length > 0 && documents.every((doc) => checked[doc.key]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allChecked) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/documents-statutaires/accepter`,
        { documentKeys: documents.map((d) => d.key) }
      );
      await refreshUser();
      const from = location.state?.from;
      const fallback =
        user?.role === 'admin' || user?.role === 'super_admin'
          ? '/admin/dashboard'
          : '/dashboard';
      navigate(from && from !== '/acceptation-documents' ? from : fallback, {
        replace: true,
      });
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'Une erreur est survenue lors de l\'enregistrement.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <img src="/logo.png" alt="Abeille Réunion" className="h-12 w-auto" />
          <button
            type="button"
            onClick={handleLogout}
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" />
            Déconnexion
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-10">
        <h1 className="text-2xl font-semibold text-slate-900">
          Nouveaux documents statutaires
        </h1>
        <p className="mt-3 text-slate-600">
          Les statuts et règlements intérieurs de vos groupements ont été mis à jour.
          Merci de les lire et de cocher chaque case pour continuer à utiliser la plateforme.
        </p>

        {loading ? (
          <p className="mt-8 text-slate-500">Chargement des documents…</p>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            {Object.entries(grouped).map(([organisme, docs]) => {
              const meta = ORGANISME_META[organisme] || {
                nom: organisme,
                heading: 'text-slate-800',
                card: 'border-slate-200 bg-white',
              };
              return (
                <section
                  key={organisme}
                  className={`rounded-xl border p-5 ${meta.card}`}
                >
                  <h2 className={`text-lg font-semibold mb-4 ${meta.heading}`}>
                    {meta.nom}
                  </h2>
                  <div className="space-y-4">
                    {docs.map((doc) => (
                      <label
                        key={doc.key}
                        htmlFor={doc.key}
                        className="flex items-start gap-3 bg-white/80 rounded-lg p-3 border border-white"
                      >
                        <input
                          id={doc.key}
                          type="checkbox"
                          checked={Boolean(checked[doc.key])}
                          onChange={(e) =>
                            setChecked((prev) => ({
                              ...prev,
                              [doc.key]: e.target.checked,
                            }))
                          }
                          className="mt-1 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <span className="text-sm text-slate-800">
                          J&apos;atteste avoir pris connaissance du document{' '}
                          <a
                            href={doc.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:text-indigo-800 underline font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {doc.label}
                          </a>
                          <span className="text-red-600"> *</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </section>
              );
            })}

            {error && (
              <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={!allChecked || submitting}
              className="w-full sm:w-auto px-6 py-3 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Enregistrement…' : 'J’accepte ces documents'}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
